import React, { useState, useCallback, useEffect } from "react";
import {
    Container,
    Grid,
    Button,
    FormControl,
    Typography,
    TextField,
    MenuItem
} from "@mui/material";
import SaveIcon from '@mui/icons-material/Save';
import LockIcon from '@mui/icons-material/Lock';
import RichText from '../../../../common/EditorMCE'
import { useNavigate } from 'react-router-dom';
import { UpdateUser, Logout } from '../../../../api'
import { validate } from 'validate.js';
import constraints from '../../../AccountAccess/validate-email-constraints';

let signatureRef;

const isEditorAlive = editor => editor && !editor.destroyed && editor.getBody()
const isEditorEmpty = editor => {
  if (!isEditorAlive(editor)) return true
  return editor.getContent({ format: 'text' }).trim() === '' && !editor.getBody().querySelector('img');
}

const UserSettings = ({ user, setUser }) => {

    const [passValues, setPassValues] = useState({})
    const [userValues, setUserValues] = useState({})
    const [submitError, setSubmitError] = useState()

    const navigate = useNavigate();

    const updatePass = e => {
        setPassValues(p => ({ ...p, [e.target.name]: e.target.value }))
    }

    const updateUser = e => {
        setUserValues(p => ({ ...p, [e.target.name]: e.target.value }))
    }

    const handlePassSubmit = useCallback(e => {
        e.preventDefault()
        if (!passValues.password || passValues.password.length <= 5)
            setSubmitError('New password must be at least 5 characters')
        else if (passValues.password === passValues.confirmPassword)
            UpdateUser(null, passValues).then(() => Logout()).then(() => navigate('/')).catch(e => {
                const error = (e.response && e.response.data && e.response.data.error) || e.message
                setSubmitError(error)
            })
        else
            setSubmitError('New password and confirm password do not match')
    }, [passValues, navigate])

    const handleUserSubmit = useCallback(e => {
        e.preventDefault()
        const emailAddress = userValues.email?.toLowerCase().trim()
        const validation = validate({ emailAddress }, constraints)
        if (validation) { setSubmitError(validation.emailAddress); return }
        userValues.signature = (signatureRef?.current && !isEditorEmpty(signatureRef.current) && signatureRef.current.getContent()) || null
        UpdateUser(null, { ...userValues, email: emailAddress }).then(() =>
            setUser(e => ({ ...e, email: emailAddress, name: userValues.name, suffix: userValues.suffix, phone: userValues.phone, org: userValues.org, signature: userValues.signature }))
        ).then(() => navigate('/'))
    }, [userValues, navigate, setUser])

    useEffect(() => {
        setUserValues({ email: user.email, name: user.name, suffix: user.suffix, phone: user.phone, org: user.org?.id, signature: user.signature })
    }, [user])

    if (!userValues) return <React.Fragment />;

    return (
        <Container>
            <form onSubmit={handlePassSubmit} noValidate>
                <Grid
                    container
                    sx={{ mb: 3 }}
                    spacing={3}
                    justifyContent="left"
                    maxWidth="sm"
                >
                    <Typography component="h1" variant="h4">Update Password</Typography>
                    {submitError ? <FormControl sx={{ mb: 1 }} fullWidth variant="standard"><Typography component="p" color="error">{submitError}</Typography></FormControl> : null}
                    <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                        <TextField
                            name="oldPassword"
                            value={passValues.oldPassword || ''}
                            label="Current Password"
                            onChange={updatePass}
                            variant="standard"
                            type="password"
                            autoComplete="false"
                        />
                    </FormControl>
                    <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                        <TextField
                            name="password"
                            value={passValues.password || ''}
                            label="New Password"
                            onChange={updatePass}
                            variant="standard"
                            type="password"
                            autoComplete="false"
                        />
                    </FormControl>
                    <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                        <TextField
                            name="confirmPassword"
                            value={passValues.confirmPassword || ''}
                            label="Confirm New Password"
                            onChange={updatePass}
                            variant="standard"
                            type="password"
                            autoComplete="false"
                        />
                    </FormControl>
                </Grid>
                <Grid sx={{ mb: 5 }} container spacing={3} justifyContent="right" maxWidth="sm">
                    <Button variant="contained" color="primary" type="submit" startIcon={<LockIcon />}>
                        Save
                    </Button>
                </Grid>
            </form>
            <form onSubmit={handleUserSubmit} noValidate>
                <Grid
                    container
                    sx={{ mb: 3 }}
                    spacing={3}
                    justifyContent="left"
                    maxWidth="sm"
                >
                    <Typography component="h1" variant="h4">Update User Profile</Typography>
                    <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                        <TextField
                            name="email"
                            value={userValues.email || ''}
                            label="Email"
                            onChange={updateUser}
                            variant="standard"
                        />
                    </FormControl>
                    <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                        <TextField
                            name="name"
                            value={userValues.name || ''}
                            label="Name"
                            onChange={updateUser}
                            variant="standard"
                        />
                    </FormControl>
                    <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                        <TextField
                            name="suffix"
                            value={userValues.suffix || ''}
                            label="Suffix"
                            onChange={updateUser}
                            variant="standard"
                        />
                    </FormControl>
                    <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                        <TextField
                            name="phone"
                            value={userValues.phone || ''}
                            label="Phone Number"
                            onChange={(e) => {
                                const formatted = e.target.value.replace(/\D/g, '').replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
                                setUserValues(p => ({ ...p, phone: formatted }));
                            }}
                            variant="standard"
                            placeholder="(555) 123-4567"
                            inputProps={{ maxLength: 14 }}
                        />
                    </FormControl>
                    <FormControl sx={{ mb: 1 }} fullWidth variant="standard">
                        <TextField
                            name="org"
                            value={userValues.org || ''}
                            label="Default Organization"
                            onChange={updateUser}
                            variant="standard"
                            select
                        >
                            {user.access.map(e =>
                                <MenuItem key={e.org.id} value={e.org.id}>
                                    {e.org.name}
                                </MenuItem>
                            )}
                        </TextField>
                    </FormControl>
                    <FormControl fullWidth variant="standard">
                        <Typography component="h2" variant="h6">
                            Signature:
                        </Typography>
                        <RichText
                            onEditorRef={ref => signatureRef=ref}
                            initialValue={userValues.signature || '<p></p>'}
                        />
                    </FormControl>
                </Grid>
                <Grid container spacing={3} justifyContent="right" maxWidth="sm">
                    <Button variant="contained" color="primary" type="submit" startIcon={<SaveIcon />}>
                        Save
                    </Button>
                </Grid>
            </form>
        </Container>
    )

}

export default UserSettings